import React from 'react';
import { fetchInsights } from '../services/backendAPI';

// ── Shared insights rendering ────────────────────────────────────────────────
// Both AgentsScreen (the run-each dashboard) and InsightsScreen (the curated,
// read-only view) render the same backend analysis-agent output, so the row
// primitives, the per-agent render definitions, and the poll helper live here
// as one source of truth.

export const Row = ({ label, value, strong }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-sm)', padding: '2px 0', color: 'var(--bh-black)' }}>
    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: strong ? 700 : 400 }}>{label}</span>
    <span style={{ whiteSpace: 'nowrap', textAlign: 'right', fontWeight: strong ? 700 : 500 }}>{value}</span>
  </div>
);

export const Dim = ({ children }) => <span style={{ color: 'var(--bh-gray)', fontSize: '0.85em' }}>{children}</span>;

export const Delta = ({ v }) => (
  <span style={{ marginLeft: 6, fontSize: '0.85em', color: v > 10 ? '#c0392b' : v < -10 ? '#1b7a3d' : 'var(--bh-gray)' }}>
    {v > 0 ? '↑' : '↓'}{Math.abs(v)}%
  </span>
);

// One entry per backend analysis agent. `render(ins)` returns that agent's slice
// of the insights object, or null when the agent found nothing. Order leads with
// the spending-attribute agents, then health + narrative.
export const AGENTS = [
  {
    key: 'spendDigest', emoji: '🥧', label: 'Spend Digest',
    blurb: 'Where your money went in the last 30 days + month-over-month change',
    render: (ins) => {
      const d = ins.spendDigest;
      if (!d?.totalSpend) return null;
      return (
        <>
          <Row label="Total (last 30 days)" value={`$${d.totalSpend.toLocaleString()}`} strong />
          {d.byCategory.slice(0, 6).map((c, i) => (
            <Row key={i} label={c.category} value={<>${c.amount.toLocaleString()} <Dim>{c.pct}%</Dim>{c.changePct != null && <Delta v={c.changePct} />}</>} />
          ))}
        </>
      );
    },
  },
  {
    key: 'budgetPulse', emoji: '📊', label: 'Spending Shifts',
    blurb: 'Categories trending up or down vs your usual pace',
    render: (ins) => {
      const b = ins.budgetPulse;
      if (!b?.length) return null;
      return b.map((x, i) => (
        <Row key={i} label={x.category} value={
          <span style={{ color: x.direction === 'up' ? '#c0392b' : '#1b7a3d' }}>
            {x.direction === 'up' ? '↑' : '↓'} {Math.abs(x.changePct)}% <Dim>(${x.recent} vs ${x.priorMonthly}/mo)</Dim>
          </span>
        } />
      ));
    },
  },
  {
    key: 'subscriptions', emoji: '🔁', label: 'Recurring Charges',
    blurb: 'Subscriptions, memberships and other recurring charges',
    render: (ins) => {
      const s = ins.subscriptions;
      if (!s?.length) return null;
      const total = Math.round(s.reduce((a, r) => a + (r.monthlyEst || 0), 0));
      return (
        <>
          <Row label="Estimated monthly total" value={`~$${total.toLocaleString()}/mo`} strong />
          {s.slice(0, 8).map((r, i) => (
            <Row key={i} label={r.merchant} value={<>${r.amount.toFixed(2)} <Dim>{r.frequency}</Dim></>} />
          ))}
        </>
      );
    },
  },
  {
    key: 'anomalies', emoji: '⚠️', label: 'Unusual Charges',
    blurb: 'Recent charges well above your normal for that category',
    render: (ins) => {
      const a = ins.anomalies;
      if (!a?.length) return null;
      return a.map((x, i) => (
        <Row key={i} label={x.description} value={
          <span style={{ color: '#c0392b' }}>${x.amount.toFixed(2)} <Dim>{x.multiple}× usual</Dim></span>
        } />
      ));
    },
  },
  {
    key: 'healthScore', emoji: '❤️', label: 'Financial Health',
    blurb: 'Eight indicators of financial wellbeing',
    render: (ins) => {
      const h = ins.healthScore;
      if (!h?.indicators?.length) return null;
      const dot = (s) => s === 'healthy' ? '#1b7a3d' : s === 'coping' ? '#d99100' : '#c0392b';
      return (
        <>
          <Row label="Score" value={`${h.score} / ${h.maxScore} healthy`} strong />
          {h.indicators.map((ind, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-sm)', padding: '2px 0', alignItems: 'baseline' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--bh-black)', minWidth: 0 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot(ind.status), flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ind.label}</span>
              </span>
              <Dim>{ind.detail}</Dim>
            </div>
          ))}
        </>
      );
    },
  },
  {
    key: 'narrative', emoji: '✨', label: 'AI Summary',
    blurb: 'Plain-English observations from the numbers',
    render: (ins) => {
      const n = ins.narrative;
      if (!n?.length) return null;
      return n.map((obs, i) => (
        <div key={i} style={{ color: 'var(--bh-black)', lineHeight: 1.5, padding: '2px 0' }}>· {obs}</div>
      ));
    },
  },
];

export const ALL_KEYS = AGENTS.map(a => a.key);
export const AGENT_BY_KEY = Object.fromEntries(AGENTS.map(a => [a.key, a]));

// Poll the server until the initial/triggered run finishes (insights.ready).
// Returns the latest payload regardless once tries are exhausted.
export async function pollUntilReady(sid, tries = 30, delay = 700) {
  for (let i = 0; i < tries; i++) {
    const data = await fetchInsights(sid);
    if (data?.ready) return data;
    await new Promise(r => setTimeout(r, delay));
  }
  return fetchInsights(sid);
}

// After this long without a re-run, an agent's result is shown as possibly stale.
export const STALE_AFTER_MS = 15 * 60 * 1000;

// "just now" / "Xm ago" / "Xh ago" from a server agentMeta timestamp. `now` is
// passed in so a ticking clock in the caller keeps it fresh without re-fetching.
export function timeAgo(ts, now) {
  if (!ts) return null;
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
